/* eslint-disable max-lines-per-function */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { createSupabaseClient } from '@/integrations/supabase/client';

import { ContextSelectionType } from '@/components/chat/ContextSelectionContext';

export interface SuggestionItem {
  id: string;
  title: string;
  toolType: string;
  type: ContextSelectionType;
  updatedAt: string;
}

interface ToolVersionRow {
  id: string;
  version_name: string;
  tool_id?: string | null;
  updated_at: string;
}

interface ProductRow {
  id: string;
  name: string;
  updated_at: string;
}

export const useSuggestions = () => {
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, getToken } = useAuth();

  const supabase = useMemo(() => createSupabaseClient(getToken), [getToken]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!user) {
        return;
      }

      setLoading(true);
      try {
        const [toolVersions, products] = await Promise.all([
          supabase
            .from('tool_versions')
            .select('*')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false })
            .limit(5),
          supabase
            .from('products')
            .select('*')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false })
            .limit(3),
        ]);

        const suggestionsItems: SuggestionItem[] = [];

        toolVersions?.data?.forEach((version: ToolVersionRow) => {
          const toolName =
            version.tool_id
              ?.replace(/-/g, ' ')
              ?.replace(/\b\w/g, (toolId: string) => toolId.toUpperCase()) ||
            'Tool';

          suggestionsItems.push({
            id: version.id,
            title: version.version_name,
            toolType: toolName,
            type: ContextSelectionType.Tool,
            updatedAt: version.updated_at,
          });
        });

        products?.data?.forEach((product: ProductRow) => {
          const toolName =
            product.name
              ?.replace(/-/g, ' ')
              ?.replace(/\b\w/g, (toolId: string) => toolId.toUpperCase()) ||
            'Product';

          suggestionsItems.push({
            id: product.id,
            title: product.name,
            toolType: toolName,
            type: ContextSelectionType.Product,
            updatedAt: product.updated_at,
          });
        });

        const sortedSuggestions = suggestionsItems
          .slice()
          .sort(
            (firstItem, secondItem) =>
              new Date(secondItem.updatedAt).getTime() -
              new Date(firstItem.updatedAt).getTime()
          );

        setSuggestions(sortedSuggestions.slice(0, 3));
      } catch (error) {
        console.error('Error fetching user suggestions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [supabase, user]);

  return { suggestions, loading };
};
